import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { btn, field } from "@/components/ui-kit";
import { UnmatchedDepots } from "@/components/partners/UnmatchedDepots";

const HEAD = [["ext", "File extension (TEO, AMZ)"], ["sold_to", "SAP sold-to (AG)"], ["bill_to", "SAP bill-to (RE)"], ["bill_to_gln", "Bill-to GLN / ANA"],
  ["order_type", "SAP order type"], ["sales_org", "Sales org"], ["distr_chan", "Distribution channel"], ["division", "Division"], ["currency", "Currency"], ["text_id", "Header text ID (Z008)"]];
const DP = [["gln", "Delivery GLN"], ["depot_code", "Depot code"], ["sap_ship_to", "SAP ship-to"], ["name", "Name"], ["street", "Street"], ["street2", "Street 2"], ["city", "Town"], ["county", "County"], ["postcode", "Postcode"]];
const L = ({ children }) => <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{children}</span>;

const DpTable = ({ rows, set }) => {
  const upd = (i, k, v) => set(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="idoc-dp-table">
      {rows.length > 0 && (
        <div className="hidden gap-1.5 px-2 sm:grid sm:grid-cols-[repeat(9,minmax(0,1fr))_40px]">
          {DP.map(([k, l]) => <span key={k} className="truncate font-mono text-[10px] uppercase text-slate-500">{l}</span>)}<span />
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid gap-1.5 rounded-sm border border-slate-800 p-2 sm:grid-cols-[repeat(9,minmax(0,1fr))_auto]">
          {DP.map(([k, l]) => <Input key={k} value={r[k] || ""} placeholder={l} onChange={(e) => upd(i, k, e.target.value)} className={`h-8 text-xs ${field}`} data-testid={`idoc-dp-${i}-${k}`} />)}
          <Button size="sm" variant="ghost" onClick={() => set(rows.filter((_, j) => j !== i))} className={btn.danger} data-testid={`idoc-dp-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => set([...rows, {}])} className={btn.ghost} data-testid="idoc-dp-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add delivery point</Button>
    </div>
  );
};

const keyOf = (d) => (d.gln || d.depot_code || "").trim().toUpperCase();

const dl = async (url, name) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const DpUpload = ({ rows, set, partner, dirty }) => {
  const [replace, setReplace] = useState(false);
  const template = () => dl("/idoc/delivery-points/template", "Delivery_Points_Template.csv");
  const download = () => {
    if (dirty) toast.warning("Downloading the saved list - unsaved changes in this window are not included");
    dl(`/partners/${partner.code}/idoc/delivery-points.xlsx`, `${partner.name.replace(/[^A-Za-z0-9]+/g, "_")}_Delivery_Points.xlsx`);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/idoc/delivery-points/read", fd);
      const base = replace ? [] : rows;
      const byKey = new Map(base.map((d) => [keyOf(d), d]));
      let updated = 0;
      data.rows.forEach((d) => { if (byKey.has(keyOf(d))) updated += 1; byKey.set(keyOf(d), d); });
      set([...byKey.values()]);
      toast.success(`${data.rows.length} delivery point(s) read - ${data.rows.length - updated} new, ${updated} updated${replace ? " (list replaced)" : ""}. Click Save to keep them.`);
      if (data.skipped.length) toast.warning(`Skipped ${data.skipped.length}: ${data.skipped.slice(0, 5).join("; ")}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mb-2 flex flex-wrap items-center gap-3" data-testid="idoc-dp-upload">
      <label className={`inline-flex h-8 cursor-pointer items-center rounded-md px-3 text-xs ${btn.ghost}`} data-testid="idoc-dp-upload-label">
        <Upload className="mr-1 h-3.5 w-3.5" /> Upload depot list (Excel / CSV)
        <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={upload} data-testid="idoc-dp-upload-input" />
      </label>
      <Button size="sm" variant="ghost" onClick={template} className={btn.ghost} data-testid="idoc-dp-template-btn"><Download className="mr-1 h-3.5 w-3.5" /> Template</Button>
      <Button size="sm" variant="ghost" onClick={download} className={btn.ghost} data-testid="idoc-dp-download-btn"><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Download current list</Button>
      <label className="flex items-center gap-2 text-xs text-slate-300"><Switch checked={replace} onCheckedChange={setReplace} data-testid="idoc-dp-replace" /> Replace the whole list (otherwise add / update by GLN or depot code)</label>
    </div>
  );
};

const retry = async (partner, onDone) => {
  try {
    const { data } = await api.post(`/partners/${partner.code}/idoc/retry`);
    onDone();
    if (data.created.length) toast.success(`${data.created.length} SAP IDoc(s) created: ${data.created.slice(0, 5).map((c) => c.filename).join(", ")}${data.created.length > 5 ? "…" : ""}`);
    if (data.failed.length) toast.warning(`${data.failed.length} order(s) still waiting - ${data.failed.slice(0, 3).map((x) => `${x.po_number}: ${x.error}`).join(" | ")}`, { duration: 15000 });
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const offerRetry = async (partner, enabled, onDone) => {
  if (!enabled) return;
  const { data } = await api.get(`/partners/${partner.code}/idoc/waiting`);
  if (!data.count) return;
  toast(`${data.count} order(s) are waiting for a SAP IDoc`, {
    description: `e.g. ${data.rows.slice(0, 3).map((r) => r.po_number).join(", ")}`,
    duration: 30000,
    action: { label: `Create ${data.count} IDoc(s)`, onClick: () => retry(partner, onDone) },
  });
};

export const IdocDialog = ({ partner, onOpenChange, onSaved }) => {
  const [f, setF] = useState(null);
  const [saved, setSaved] = useState("");
  useEffect(() => {
    setF(null);
    if (partner) api.get(`/partners/${partner.code}/idoc`).then((r) => { setF(r.data); setSaved(JSON.stringify(r.data.delivery_points)); }).catch((e) => toast.error(errMsg(e)));
  }, [partner]);
  const save = async () => {
    const bad = (f.delivery_points || []).filter((d) => !(d.sap_ship_to || "").trim() || !((d.gln || "").trim() || (d.depot_code || "").trim()));
    if (bad.length) {
      toast.error(`${bad.length} delivery point(s) need a SAP ship-to and a GLN or depot code: ${bad.slice(0, 5).map((d) => d.gln || d.name || "blank row").join(", ")}`);
      return;
    }
    try {
      await api.put(`/partners/${partner.code}/idoc`, f);
      toast.success(`${partner.name}: SAP IDoc settings saved`);
      onOpenChange(false);
      onSaved();
      offerRetry(partner, f.enabled, onSaved);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!partner} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto border-slate-800 bg-[#1F232C] text-slate-100" data-testid="idoc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">SAP order IDoc - {partner?.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Each customer order received from this partner is written as an SAP ORDERS05 IDoc (e.g. rx032310.{f?.ext || "EXT"}) to the IDoc folder on the SAP SFTP. The ship-to is found by matching the order's delivery GLN or depot code to the table below.</DialogDescription>
        </DialogHeader>
        {f && partner && (
          <div className="space-y-5">
            <label className="flex items-center gap-3 text-sm text-slate-200"><Switch checked={!!f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="idoc-enabled" /> Create SAP IDoc for every order from this partner</label>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {HEAD.map(([k, l]) => <label key={k} className="block"><L>{l}</L><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} data-testid={`idoc-field-${k}`} /></label>)}
              <label className="block"><L>Header text content</L>
                <select value={f.text_mode || "none"} onChange={(e) => setF({ ...f, text_mode: e.target.value })} className={`h-9 w-full rounded-md border px-2 text-sm ${field}`} data-testid="idoc-field-text_mode">
                  <option value="none">No header text</option><option value="delivery_date">Delivery date (YYYYMMDD 000000)</option><option value="notes">Order notes</option>
                </select>
              </label>
              {[["price_condition", "Price condition type (E2EDP05) - blank = price in E2EDP01"], ["text_notes_id", "Note to Supplier text ID (Customer Notes)"], ["text_booking_id", "Booking ref text ID (EDI Booking In Ref No)"], ["text_slot_id", "Delivery date / time text ID (EDI Date and Time Slot)"]].map(([k, l]) => (
                <label key={k} className="block"><L>{l}</L><Input value={f[k] || ""} placeholder="SAP text ID e.g. Z010" onChange={(e) => setF({ ...f, [k]: e.target.value.toUpperCase() })} className={field} data-testid={`idoc-field-${k}`} /></label>))}
              <label className="block lg:col-span-2"><L>Other SAP accounts for deliveries (sold-to / ship-to, comma separated)</L><Input value={f.delivery_accounts || ""} placeholder="e.g. 7006, 26069" onChange={(e) => setF({ ...f, delivery_accounts: e.target.value })} className={field} data-testid="idoc-field-delivery_accounts" /></label>
              <label className="block lg:col-span-2"><L>SAP delivery IDoc creates</L>
                <select value={f.delivery_route || ""} onChange={(e) => setF({ ...f, delivery_route: e.target.value })} className={`h-9 w-full rounded-sm px-2 text-sm ${field}`} data-testid="idoc-field-delivery_route">
                  <option value="">Ship notice to this partner (856 / DESADV / 943)</option>
                  <option value="940">940 Warehouse Shipping Order to Group O (Group O ships it)</option>
                </select></label>
            </div>
            <div><L>Delivery points ({(f.delivery_points || []).length})</L><UnmatchedDepots partner={partner} rows={f.delivery_points || []} set={(v) => setF({ ...f, delivery_points: v })} /><DpUpload rows={f.delivery_points || []} set={(v) => setF({ ...f, delivery_points: v })} partner={partner} dirty={JSON.stringify(f.delivery_points) !== saved} /><DpTable rows={f.delivery_points || []} set={(v) => setF({ ...f, delivery_points: v })} /></div>
            <Button onClick={save} className={btn.primary} data-testid="idoc-save-btn">Save IDoc settings</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
